import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Download, FileImage, FileText, Table } from 'lucide-react';
import { motion } from 'framer-motion';

const ExportCenter = ({ reducedMotion = false }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [loading, setLoading] = useState({});
  const animationDuration = reducedMotion ? 0 : 0.3;

  const handleExport = async (type, chartId = null) => {
    const key = chartId || type;
    setLoading(prev => ({ ...prev, [key]: true }));

    try {
      let response;
      if (type === 'png') {
        response = await fetch(`${BACKEND_URL}/api/export/dashboard/png`, { method: 'POST' });
      } else if (type === 'pdf') {
        response = await fetch(`${BACKEND_URL}/api/export/dashboard/pdf`, { method: 'POST' });
      } else if (type === 'csv') {
        response = await fetch(`${BACKEND_URL}/api/export/csv?chart=${chartId}`);
      }

      const result = await response.json();
      
      if (result.download_url) {
        // In production, would trigger download
        alert(`Export ready! ${result.download_url}`);
      }
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const ExportButton = ({ icon: Icon, label, onClick, isLoading, color }) => (
    <motion.button
      onClick={onClick}
      disabled={isLoading}
      className={`flex items-center gap-3 p-4 rounded-lg border transition-all ${
        isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:border-gold/40 hover:bg-slate-800/50'
      } bg-slate-900/50 border-slate-700`}
      whileHover={reducedMotion || isLoading ? {} : { scale: 1.02 }}
      whileTap={reducedMotion || isLoading ? {} : { scale: 0.98 }}
    >
      <div className="p-2 rounded" style={{ backgroundColor: color + '20' }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="flex-1 text-left">
        <p className="text-sm font-medium text-slate-100">{label}</p>
        <p className="text-xs text-slate-400 mt-0.5">
          {isLoading ? 'Generating...' : 'Click to export'}
        </p>
      </div>
      {isLoading && (
        <div className="animate-spin w-4 h-4 border-2 border-gold/40 border-t-transparent rounded-full" />
      )}
    </motion.button>
  );

  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
          <Download className="w-5 h-5 text-ink-500" />
          Export Center
        </CardTitle>
        <p className="text-xs text-slate-400 mt-1">
          Export dashboard and charts in multiple formats
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Dashboard Exports */}
          <div>
            <h4 className="text-sm font-medium text-slate-300 mb-3">Full Dashboard</h4>
            <div className="grid grid-cols-2 gap-3">
              <ExportButton
                icon={FileImage}
                label="Export as PNG"
                onClick={() => handleExport('png')}
                isLoading={loading.png}
                color="#C9A24A"
              />
              <ExportButton
                icon={FileText}
                label="Export as PDF"
                onClick={() => handleExport('pdf')}
                isLoading={loading.pdf}
                color="#6B7280"
              />
            </div>
          </div>

          {/* Chart CSV Exports */}
          <div>
            <h4 className="text-sm font-medium text-slate-300 mb-3">Chart Data (CSV)</h4>
            <div className="space-y-2">
              {[
                { id: 'hourly-heatmap', name: 'Hourly Heatmap' },
                { id: 'call-funnel', name: 'Call Journey Funnel' },
                { id: 'latency-over-time', name: 'Latency Over Time' },
                { id: 'asr-performance', name: 'ASR Performance' },
                { id: 'variant-comparison', name: 'Variant Comparison' },
                { id: 'sentiment-trend', name: 'Sentiment Trendline' }
              ].map((chart) => (
                <ExportButton
                  key={chart.id}
                  icon={Table}
                  label={chart.name}
                  onClick={() => handleExport('csv', chart.id)}
                  isLoading={loading[chart.id]}
                  color="#10b981"
                />
              ))}
            </div>
          </div>

          {/* Info */}
          <div className="mt-6 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <p className="text-xs text-slate-400">
              <strong className="text-slate-300">Note:</strong> Exports include current filter settings and visible date range. 
              PNG and PDF exports reflect the current dashboard theme and layout.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ExportCenter;
